import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { Table, Button, Modal, Form, Alert } from 'react-bootstrap';
import FilterComponent, { FilterOption, AppliedFilter } from '../../../components/common/FilterComponent';
import PaginationComponent from '../../../components/common/PaginationComponent';
import { userService, User } from '../../../services/userService';

interface CreateUserRequestForm {
    documento?: string;
    nome?: string;
    sobrenome?: string;
    email?: string;
    telefone?: string;
    status?: string;
}

const UserAdminPage: React.FC = () => {
    const history = useHistory();
    const [users, setUsers] = useState<User[]>([]);
    const [pagination, setPagination] = useState<PaginationInfo>({
        page: 0,
        size: 10,
        total: 0,
        totalPages: 0
    });
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [showUserModal, setShowUserModal] = useState<boolean>(false);
    const [showAddUserModal, setShowAddUserModal] = useState<boolean>(false);
    const [selectedUser, setSelectedUser] = useState<User | null>(null);
    const [formData, setFormData] = useState<CreateUserRequestForm>({});
    const [showFilter, setShowFilter] = useState(false);
    const [filterText, setFilterText] = useState('');
    const [filterType, setFilterType] = useState<'name' | 'email' | 'status'>('name');
    const [appliedFilters, setAppliedFilters] = useState<AppliedFilter[]>([]);
    const [isAddingUser, setIsAddingUser] = useState<boolean>(false);
    const [successMessage, setSuccessMessage] = useState<string | null>(null);
    const [documentoError, setDocumentoError] = useState<string | null>(null);

    const filterOptions: FilterOption[] = [
        { value: 'name', label: 'Nome' },
        { value: 'email', label: 'Email' },
        { value: 'status', label: 'Status' }
    ];

    // 1. Update User type
    interface PaginationInfo {
        page: number;
        size: number;
        total: number;
        totalPages: number;
    }

    // 3. Fetch users from API
    const fetchUsers = async (filters?: { nome?: string; email?: string; status?: string }, page?: number, size?: number) => {
        try {
            setLoading(true);
            const apiFilters = {
                ...filters,
                page: page !== undefined ? page : pagination.page,
                size: size !== undefined ? size : pagination.size,
            };
            const data = await userService.getUsers(apiFilters);
            setUsers(data.content || []);
            setPagination(prev => ({
                ...prev,
                page: apiFilters.page,
                size: apiFilters.size,
                total: data.totalElements || data.totalElements || 0,
                totalPages: data.totalPages || Math.ceil((data.totalElements || data.totalElements || 0) / apiFilters.size)
            }));
            setError(null);
        } catch (err) {
            setError('Failed to fetch users. Please try again later.');
            console.error('Error fetching users:', err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchUsers();
    }, []);

    const handleAddUserClick = () => {
        setFormData({
            nome: '',
            sobrenome: '',
            email: '',
            status: 'ATIVO'
        });
        setShowAddUserModal(true);
    };

    const handleEditUserClick = (user: User) => {
        setSelectedUser(user);
        setFormData({
            documento: user.documento || '',
            nome: user.nome || '',
            sobrenome: user.sobrenome || '',
            email: user.email || '',
            telefone: user.telefone || '',
            status: user.status || ''
        });
        setShowUserModal(true);
    };

    const handleEditAccessClick = (user: User) => {
        history.push(`/admin/users/${user.usuario_id}/access`);
    };

    const handleUserModalClose = () => {
        setShowUserModal(false);
        setShowAddUserModal(false);
        setSelectedUser(null);
        setFormData({});
        setIsAddingUser(false);
        setDocumentoError(null);
    };

    const formatCpf = (value: string) => {
        // Remove all non-digits
        const digits = value.replace(/\D/g, '');
        // Apply mask: 000.000.000-00
        return digits
            .replace(/(\d{3})(\d)/, '$1.$2')
            .replace(/(\d{3})(\d)/, '$1.$2')
            .replace(/(\d{3})(\d{1,2})$/, '$1-$2')
            .slice(0, 14);
    };

    const handleInputChange = (
        e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
    ) => {
        const { name, value } = e.target;
        if (name === 'documento') {
            setFormData(prev => ({
                ...prev,
                [name]: formatCpf(value)
            }));
            // Do not validate here
            setDocumentoError(null);
        } else {
            setFormData(prev => ({
                ...prev,
                [name]: value
            }));
        }
    };


    const handleSubmitUser = async (e: React.FormEvent) => {
        e.preventDefault();

        // Validate CPF before submission
        if (formData.documento) {
            const numericValue = formData.documento.replace(/\D/g, '');
            if (numericValue.length !== 11) {
                setDocumentoError('CPF deve ter exatamente 11 dígitos');
                return;
            }
            const digits = numericValue.split('');
            const allSame = digits.every(digit => digit === digits[0]);
            if (allSame) {
                setDocumentoError('CPF inválido');
                return;
            }
        }

        if (showAddUserModal) {
            try {
                setIsAddingUser(true);
                setError(null);
                setSuccessMessage(null);

                // Collect required fields for user creation
                const newUserPayload = {
                    documento: formData.documento ? formData.documento.replace(/\D/g, '') : '',
                    nome: formData.nome || '',
                    sobrenome: formData.sobrenome || '',
                    email: formData.email || '',
                    telefone: formData.telefone || '',
                };
                await userService.createUser(newUserPayload);
                await fetchUsers();
                setSuccessMessage('Usuário criado com sucesso!');
                handleUserModalClose();
            } catch (err) {
                setError('Failed to add user. Please try again later.');
                console.error('Error adding user:', err);
            } finally {
                setIsAddingUser(false);
            }
        } else if (selectedUser) {
            try {
                // Call API to update user
                const updateUserPayload = {
                    nome: formData.nome || '',
                    sobrenome: formData.sobrenome || '',
                    telefone: formData.telefone || '',
                    documento: formData.documento ? formData.documento.replace(/\D/g, '') : '',
                    email: formData.email || '',
                    status: formData.status || ''
                };

                await userService.editUser(selectedUser.usuario_id, updateUserPayload);

                await fetchUsers();
                setSuccessMessage('Usuário atualizado com sucesso!');
                handleUserModalClose();
            } catch (err) {
                setError('Failed to update user. Please try again later.');
                console.error('Error updating user:', err);
            }
        }
    };

    const handleFilterApply = (filterText: string, filterType: string) => {
        const typedFilterType = filterType as 'name' | 'email' | 'status';

        // Only apply if filter text is not empty
        if (!filterText.trim()) return;

        // Add to applied filters
        const filterOption = filterOptions.find(opt => opt.value === typedFilterType);
        const newAppliedFilter: AppliedFilter = {
            type: typedFilterType,
            value: filterText.trim(),
            label: filterOption?.label || typedFilterType
        };

        // Remove existing filter of same type if exists
        const updatedFilters = appliedFilters.filter(f => f.type !== typedFilterType);
        setAppliedFilters([...updatedFilters, newAppliedFilter]);

        // Clear the input after applying
        setFilterText('');

        // Apply the filter to backend
        applyFiltersToBackend([...updatedFilters, newAppliedFilter]);
    };

    const handleFilterRemove = (filterType: string) => {
        const updatedFilters = appliedFilters.filter(f => f.type !== filterType);
        setAppliedFilters(updatedFilters);
        applyFiltersToBackend(updatedFilters);
    };

    const applyFiltersToBackend = (filters: AppliedFilter[]) => {
        const backendFilters: any = {};

        if (filters.length > 0) {
            filters.forEach(filter => {
                if (filter.type === 'name') backendFilters.nome = filter.value;
                if (filter.type === 'email') backendFilters.email = filter.value;
                if (filter.type === 'status') backendFilters.status = filter.value;
            });
        }

        fetchUsers(backendFilters, 0, pagination.size);
        setPagination(prev => ({ ...prev, page: 0 }));
    };

    const handleFilterClear = () => {
        setFilterText('');
        setAppliedFilters([]);
        fetchUsers({}, 0, pagination.size);
        setPagination(prev => ({ ...prev, page: 0 }));
    };

    const handlePageChange = (newPage: number) => {
        const backendFilters: any = {};

        if (appliedFilters.length > 0) {
            appliedFilters.forEach(filter => {
                if (filter.type === 'name') backendFilters.nome = filter.value;
                if (filter.type === 'email') backendFilters.email = filter.value;
                if (filter.type === 'status') backendFilters.status = filter.value;
            });
        }

        fetchUsers(backendFilters, newPage, pagination.size);
        setPagination(prev => ({ ...prev, page: newPage }));
    };

    const handlePageSizeChange = (newSize: number) => {
        const backendFilters: any = {};

        if (appliedFilters.length > 0) {
            appliedFilters.forEach(filter => {
                if (filter.type === 'name') backendFilters.nome = filter.value;
                if (filter.type === 'email') backendFilters.email = filter.value;
                if (filter.type === 'status') backendFilters.status = filter.value;
            });
        }

        fetchUsers(backendFilters, 0, newSize);
        setPagination(prev => ({ ...prev, page: 0, size: newSize }));
    };

    if (loading) {
        return <div className="text-center p-5">Loading...</div>;
    }

    return (
        <div className="container-fluid p-4">
            <Button
                variant="outline-secondary"
                onClick={() => history.push('/admin')}
                className="return-button mb-3 d-flex align-items-center gap-2"
            >
                <span>←</span> Voltar
            </Button>
            <div className="d-flex justify-content-between align-items-center mb-4">
                <h2>Gerenciamento de Usuários</h2>
                <Button variant="primary" onClick={handleAddUserClick}>
                    Adicionar Usuário
                </Button>
            </div>

            {!showFilter && (
                <div className="mb-2">
                    <Button
                        variant="outline-secondary"
                        onClick={() => setShowFilter(true)}
                        className="filter-toggle-button d-inline-flex align-items-center gap-2"
                        aria-expanded={showFilter}
                    >
                        <span>🔍</span>
                        Filtrar
                    </Button>
                </div>
            )}
            {showFilter && (
                <div className="mb-3">
                    <FilterComponent
                        showFilter={showFilter}
                        filterText={filterText}
                        filterType={filterType}
                        filterOptions={filterOptions}
                        appliedFilters={appliedFilters}
                        onFilterApply={handleFilterApply}
                        onFilterTypeChange={(type) => setFilterType(type as 'name' | 'email' | 'status')}
                        onFilterTextChange={setFilterText}
                        onFilterTextClear={handleFilterClear}
                        onFilterToggle={() => setShowFilter(false)}
                        onFilterRemove={handleFilterRemove}
                        placeholderText={`Buscar por ${filterType === 'name' ? 'nome' : filterType === 'email' ? 'email' : 'status'}...`}
                    />
                </div>
            )}

            {error && (
                <Alert variant="danger" onClose={() => setError(null)} dismissible>
                    {error}
                </Alert>
            )}
            {successMessage && (
                <Alert variant="success" onClose={() => setSuccessMessage(null)} dismissible>
                    {successMessage}
                </Alert>
            )}
            <div className="table-responsive">
                <Table striped bordered hover>
                    <thead>
                        <tr>
                            <th>Nome</th>
                            <th>Sobrenome</th>
                            <th>Email</th>
                            <th>Status</th>
                            <th>Ações</th>
                        </tr>
                    </thead>
                    <tbody>
                        {users.map(user => (
                            <tr key={user.usuario_id}>
                                <td>{user.nome}</td>
                                <td>{user.sobrenome}</td>
                                <td>{user.email}</td>
                                <td>
                                    <span className={`badge ${user.status === 'ATIVO' ? 'bg-success' : 'bg-danger'}`}>
                                        {user.status}
                                    </span>
                                </td>
                                <td>
                                    <div className="d-flex gap-2">
                                        <Button
                                            variant="outline-secondary"
                                            size="sm"
                                            onClick={() => {
                                                handleEditUserClick(user);
                                            }}
                                            disabled={loading}
                                        >
                                            Editar Usuário
                                        </Button>
                                        <Button
                                            variant="outline-secondary"
                                            size="sm"
                                            onClick={() => handleEditAccessClick(user)}
                                            disabled={loading}
                                            className="d-flex align-items-center border border-secondary text-secondary"
                                        >
                                            <i className="mdi mdi-account-key me-1" style={{ color: '#966A2E' }}></i>
                                            Gerenciar Acessos e Perfis
                                        </Button>
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </Table>
            </div>

            {/* Pagination Controls */}
            <PaginationComponent
                page={pagination.page}
                size={pagination.size}
                total={pagination.total}
                totalPages={pagination.totalPages}
                onPageChange={handlePageChange}
                onPageSizeChange={handlePageSizeChange}
                entityLabel="usuários"
            />

            {/* User Edit Modal */}
            <Modal
                show={showUserModal}
                onHide={handleUserModalClose}
                key={`edit-modal-${selectedUser?.usuario_id || 'new'}`}
            >
                <Modal.Header>
                    <Modal.Title>Editar Usuário</Modal.Title>
                </Modal.Header>
                <Form onSubmit={handleSubmitUser}>
                    <Modal.Body>
                        <Form.Group className="mb-3">
                            <Form.Label>Documento (CPF)</Form.Label>
                            <Form.Control
                                type="text"
                                name="documento"
                                value={formData.documento || ''}
                                onChange={handleInputChange}
                                placeholder="000.000.000-00"
                                isInvalid={!!documentoError}
                                required
                            />
                            {documentoError && (
                                <Form.Control.Feedback type="invalid">
                                    {documentoError}
                                </Form.Control.Feedback>
                            )}
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Nome</Form.Label>
                            <Form.Control
                                type="text"
                                name="nome"
                                value={formData.nome || ''}
                                onChange={handleInputChange}
                                required
                            />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Sobrenome</Form.Label>
                            <Form.Control
                                type="text"
                                name="sobrenome"
                                value={formData.sobrenome || ''}
                                onChange={handleInputChange}
                                required
                            />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Email</Form.Label>
                            <Form.Control
                                type="email"
                                name="email"
                                value={formData.email || ''}
                                onChange={handleInputChange}
                                required
                            />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Telefone</Form.Label>
                            <Form.Control
                                type="text"
                                name="telefone"
                                value={formData.telefone || ''}
                                onChange={handleInputChange}
                            />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Status</Form.Label>
                            <Form.Control
                                as="select"
                                name="status"
                                value={formData.status || ''}
                                onChange={handleInputChange}
                                required
                            >
                                <option value="ATIVO">Ativo</option>
                                <option value="INATIVO">Inativo</option>
                            </Form.Control>
                        </Form.Group>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={handleUserModalClose}>
                            Cancelar
                        </Button>
                        <Button variant="primary" type="submit">
                            Salvar Alterações
                        </Button>
                    </Modal.Footer>
                </Form>
            </Modal>

            {/* Add User Modal */}
            <Modal show={showAddUserModal} onHide={handleUserModalClose}>
                <Modal.Header>
                    <Modal.Title>Adicionar Usuário</Modal.Title>
                </Modal.Header>
                <Form onSubmit={handleSubmitUser}>
                    <Modal.Body>
                        <Form.Group className="mb-3">
                            <Form.Label>Documento (CPF)</Form.Label>
                            <Form.Control
                                type="text"
                                name="documento"
                                value={formData.documento || ''}
                                onChange={handleInputChange}
                                placeholder="000.000.000-00"
                                isInvalid={!!documentoError}
                                required
                            />
                            {documentoError && (
                                <Form.Control.Feedback type="invalid">
                                    {documentoError}
                                </Form.Control.Feedback>
                            )}
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Nome</Form.Label>
                            <Form.Control
                                type="text"
                                name="nome"
                                value={formData.nome || ''}
                                onChange={handleInputChange}
                                required
                            />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Sobrenome</Form.Label>
                            <Form.Control
                                type="text"
                                name="sobrenome"
                                value={formData.sobrenome || ''}
                                onChange={handleInputChange}
                                required
                            />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Email</Form.Label>
                            <Form.Control
                                type="email"
                                name="email"
                                value={formData.email || ''}
                                onChange={handleInputChange}
                                required
                            />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Telefone</Form.Label>
                            <Form.Control
                                type="text"
                                name="telefone"
                                value={formData.telefone || ''}
                                onChange={handleInputChange}
                            />
                        </Form.Group>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={handleUserModalClose}>
                            Cancelar
                        </Button>
                        <Button variant="primary" type="submit" disabled={isAddingUser}>
                            {isAddingUser ? 'Adicionando...' : 'Adicionar Usuário'}
                        </Button>
                    </Modal.Footer>
                </Form>
            </Modal>
        </div>
    );
};

export default UserAdminPage;
